/**
 * Scalar type detection + date normalization.
 * Single home for: null-like vocabulary, strict scalar matchers,
 * and the lenient `parseDateValue` used by inference + date repair.
 */
import type { ColumnType } from './types';

const NULL_LIKE = new Set([
  '', 'n/a', 'na', 'null', 'nil', 'none', 'undefined', 'nan', '-', '--', '(null)', '\\n',
]);

export function isNullLike(value: string): boolean {
  return NULL_LIKE.has(value.trim().toLowerCase());
}

const RE_INT = /^[+-]?\d{1,15}$/;
const RE_FLOAT = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/;
const RE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RE_EMAIL = /^[A-Za-z0-9._%+'-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const RE_URL = /^https?:\/\/[^\s/$.?#][^\s]*$/i;
const RE_ISO = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

function isLeap(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

function validYMD(y: number, m: number, d: number): boolean {
  if (m < 1 || m > 12) return false;
  const days = [31, isLeap(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return d >= 1 && d <= days[m - 1];
}

export function isInt(v: string): boolean {
  const t = v.trim();
  if (!RE_INT.test(t)) return false;
  return Number.isSafeInteger(Number(t));
}

export function isFloat(v: string): boolean {
  const t = v.trim();
  if (!RE_FLOAT.test(t)) return false;
  if (/^[+-]?0[xXbBoO]/.test(t)) return false;
  return Number.isFinite(Number(t));
}

const BOOL_TRUE = new Set(['true', 'yes', 'y', 't']);
const BOOL_FALSE = new Set(['false', 'no', 'n', 'f']);

export function isBooleanWord(v: string): boolean {
  const t = v.trim().toLowerCase();
  return BOOL_TRUE.has(t) || BOOL_FALSE.has(t);
}

export function isUuid(v: string): boolean {
  return RE_UUID.test(v.trim());
}

export function isEmail(v: string): boolean {
  const t = v.trim();
  return t.length <= 254 && RE_EMAIL.test(t);
}

export function isUrl(v: string): boolean {
  const t = v.trim();
  if (!RE_URL.test(t)) return false;
  try {
    const u = new URL(t);
    return u.hostname.includes('.') || u.hostname === 'localhost';
  } catch {
    return false;
  }
}

export function isStrictIsoDate(v: string): boolean {
  const m = RE_ISO.exec(v.trim());
  if (!m) return false;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!validYMD(y, mo, d)) return false;
  if (m[4] !== undefined) {
    if (Number(m[4]) > 23 || Number(m[5]) > 59) return false;
    if (m[6] !== undefined && Number(m[6]) > 60) return false;
  }
  return true;
}

export interface ParsedDate {
  iso: string;
  /** 'date' for calendar-day precision, 'datetime' when a time/epoch was present. */
  kind: 'date' | 'datetime';
}

const MONTHS: Record<string, number> = {
  january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
  july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
  jan: 1, feb: 2, mar: 3, apr: 4, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12,
};

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function toIsoDate(y: number, m: number, d: number): string | null {
  if (!validYMD(y, m, d)) return null;
  return `${String(y).padStart(4, '0')}-${pad(m)}-${pad(d)}`;
}

/**
 * Lenient date parser used by inference + the normalize-dates repair.
 * Accepts ISO-8601, US `MM/DD/YYYY`, EU `DD.MM.YYYY`, `YYYY/MM/DD`,
 * month names (`April 5 2024`, `March 12, 2024`), and epoch seconds.
 * Returns null when the value is not a recognizable calendar date.
 */
export function parseDateValue(raw: string): ParsedDate | null {
  const value = raw.trim();
  if (value === '' || isNullLike(value)) return null;

  // Epoch seconds (10 digits, plausible range 1970..2100).
  if (/^-?\d{9,10}$/.test(value)) {
    const secs = Number(value);
    if (Number.isFinite(secs) && secs >= 0 && secs <= 4102444800) {
      const iso = new Date(secs * 1000).toISOString().replace(/\.\d{3}Z$/, 'Z');
      return { iso, kind: 'datetime' };
    }
    return null;
  }

  // Strict ISO first (validates leap days etc).
  if (isStrictIsoDate(value)) {
    const m = RE_ISO.exec(value)!;
    const hasTime = m[4] !== undefined;
    if (!hasTime) return { iso: `${m[1]}-${m[2]}-${m[3]}`, kind: 'date' };
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    const hh = m[4];
    const mm = m[5];
    const ss = m[6] ?? '00';
    const tz = m[7] ?? 'Z';
    void y; void mo; void d;
    // Normalize space separator + missing tz to UTC `Z`.
    const normTz = tz === 'Z' ? 'Z' : tz.includes(':') ? tz : `${tz.slice(0, 3)}:${tz.slice(3)}`;
    void normTz;
    // Rebuild via Date for canonical form, guarding invalid times.
    const candidate = new Date(`${m[1]}-${m[2]}-${m[3]}T${hh}:${mm}:${ss}${tz === undefined ? 'Z' : tz}`);
    if (Number.isNaN(candidate.getTime())) return null;
    return { iso: candidate.toISOString().replace(/\.\d{3}Z$/, 'Z'), kind: 'datetime' };
  }

  // US: MM/DD/YYYY [HH:MM[:SS]]
  let m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/.exec(value);
  if (m) {
    const iso = toIsoDate(Number(m[3]), Number(m[1]), Number(m[2]));
    if (!iso) return null;
    if (m[4] !== undefined) {
      const hh = Number(m[4]);
      const mm = Number(m[5]);
      const ss = Number(m[6] ?? '0');
      if (hh > 23 || mm > 59 || ss > 60) return null;
      return { iso: `${iso}T${pad(hh)}:${pad(mm)}:${pad(ss)}Z`, kind: 'datetime' };
    }
    return { iso, kind: 'date' };
  }

  // EU: DD.MM.YYYY
  m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(value);
  if (m) {
    const iso = toIsoDate(Number(m[3]), Number(m[2]), Number(m[1]));
    return iso ? { iso, kind: 'date' } : null;
  }

  // YYYY/MM/DD
  m = /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/.exec(value);
  if (m) {
    const iso = toIsoDate(Number(m[1]), Number(m[2]), Number(m[3]));
    return iso ? { iso, kind: 'date' } : null;
  }

  // Month names: "April 5 2024", "March 12, 2024", "5 March 2024".
  m = /^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})$/.exec(value);
  if (m) {
    const mo = MONTHS[m[1].toLowerCase()];
    if (!mo) return null;
    const iso = toIsoDate(Number(m[3]), mo, Number(m[2]));
    return iso ? { iso, kind: 'date' } : null;
  }
  m = /^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/.exec(value);
  if (m) {
    const mo = MONTHS[m[2].toLowerCase()];
    if (!mo) return null;
    const iso = toIsoDate(Number(m[3]), mo, Number(m[1]));
    return iso ? { iso, kind: 'date' } : null;
  }

  return null;
}

/** Strict single-cell classifier. Null when null-like/empty. */
export function detectType(raw: string): ColumnType | null {
  const value = raw.trim();
  if (value === '' || isNullLike(value)) return null;
  if (isBooleanWord(value)) return 'boolean';
  if (isInt(value)) return 'int';
  if (isFloat(value)) return 'float';
  if (isUuid(value)) return 'uuid';
  if (parseDateValue(value) !== null) return 'iso-date';
  if (isEmail(value)) return 'email';
  if (isUrl(value)) return 'url';
  return 'string';
}
